import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/lib/constants";
import { Bell, HelpCircle, Plus, Menu, Sun, Moon, Monitor } from "lucide-react";
import { Sidebar, BrandMark } from "@/components/layout/Sidebar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import TrialGate from "@/components/TrialGate";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import SubscriptionCelebration from "@/components/SubscriptionCelebration";

function SupportButton() {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const send = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Sujet et message requis");
    try { await api.post("/support", { subject, message }); toast.success("Demande envoyée au support"); setOpen(false); setSubject(""); setMessage(""); }
    catch { toast.error("Envoi impossible"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="support-btn" className="rounded-full p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white" title="Aide / Support"><HelpCircle className="h-5 w-5" strokeWidth={1.6} /></button>
      </DialogTrigger>
      <DialogContent className="border-zinc-800 bg-zinc-950 text-white">
        <DialogHeader><DialogTitle>Contacter le support</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="support-subject" placeholder="Sujet" value={subject} onChange={(e) => setSubject(e.target.value)} className="border-zinc-800 bg-zinc-900" />
          <Textarea data-testid="support-message" rows={5} placeholder="Décrivez votre demande…" value={message} onChange={(e) => setMessage(e.target.value)} className="border-zinc-800 bg-zinc-900" />
          <Button data-testid="support-send" onClick={send} className="w-full rounded-full bg-[#FF6A00] hover:bg-[#E65F00]">Envoyer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { key: "light", label: "Clair", icon: Sun },
    { key: "dark", label: "Sombre", icon: Moon },
    { key: "system", label: "Automatique", icon: Monitor },
  ];
  const Active = (opts.find((o) => o.key === theme) || opts[2]).icon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="theme-toggle" className="rounded-full p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white" title="Thème">
          <Active className="h-5 w-5" strokeWidth={1.6} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 border-zinc-800 bg-zinc-950 p-1 text-white">
        {opts.map((o) => (
          <button key={o.key} data-testid={`theme-${o.key}`} onClick={() => setTheme(o.key)}
            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-zinc-900 ${theme === o.key ? "text-[#FF6A00]" : "text-zinc-300"}`}>
            <o.icon className="h-4 w-4" strokeWidth={1.6} />{o.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function NotificationsBell() {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const openItem = async (n) => { if (!n.read) await api.post(`/notifications/${n.id}/read`); await load(); if (n.link) { setOpen(false); navigate(n.link); } };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="notif-bell" className="relative rounded-full p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white">
          <Bell className="h-5 w-5" strokeWidth={1.6} />
          {data.unread > 0 && <span data-testid="notif-badge" className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF6A00] px-1 text-[10px] font-bold text-white">{data.unread}</span>}
        </button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="notif-sidebar" className="flex w-full flex-col border-zinc-800 bg-zinc-950 p-0 text-white sm:max-w-md">
        <SheetHeader className="flex-row items-center justify-between space-y-0 border-b border-zinc-800 px-5 py-4">
          <SheetTitle className="text-white">Notifications{data.unread > 0 ? ` (${data.unread})` : ""}</SheetTitle>
          {data.unread > 0 && <button data-testid="notif-read-all" onClick={readAll} className="mr-6 text-xs text-[#FF6A00] hover:underline">Tout marquer lu</button>}
        </SheetHeader>
        <div className="flex-1 overflow-auto" data-testid="notif-list">
          {data.notifications.length === 0
            ? <p className="px-4 py-10 text-center text-sm text-zinc-500">Aucune notification</p>
            : data.notifications.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                className={`block w-full border-b border-zinc-900 px-5 py-3.5 text-left transition-colors hover:bg-zinc-900 ${n.read ? "opacity-60" : ""} ${n.important ? "border-l-2 border-l-[#FF6A00] bg-[#FF6A00]/5" : ""}`}>
                <p className="flex items-center gap-2 text-sm font-medium text-white">
                  {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF6A00]" />}
                  {n.type === "platform_news" && <span className="rounded bg-[#FF6A00]/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[#FF6A00]">Annonce</span>}
                  <span className="min-w-0 truncate">{n.title}</span>
                </p>
                <p className="mt-0.5 text-xs text-zinc-400">{n.body}</p>
              </button>
            ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TrialBanner({ org, user }) {
  const navigate = useNavigate();
  const b = org?.billing || {};
  if (user?.is_platform_admin || b.subscription_status !== "trial" || !b.trial_end) return null;
  const daysLeft = Math.ceil((new Date(b.trial_end).getTime() - Date.now()) / 86400000);
  if (daysLeft < 0) return null;
  return (
    <div data-testid="trial-banner" className="flex flex-wrap items-center justify-center gap-2 border-b border-[#FF6A00]/20 bg-[#FF6A00]/10 px-4 py-2 text-center text-xs text-[#FFB27A]">
      <span>{daysLeft <= 0 ? "Votre essai gratuit se termine aujourd'hui." : <>Il vous reste <strong className="text-white">{daysLeft} jour{daysLeft > 1 ? "s" : ""}</strong> d'essai gratuit.</>}</span>
      <button data-testid="trial-banner-cta" onClick={() => navigate("/app/parametres?tab=payments")} className="rounded-full bg-[#FF6A00] px-3 py-1 font-semibold text-white transition-colors hover:bg-[#E65F00]">Activer mon abonnement</button>
    </div>
  );
}

export default function Layout() {
  const { user, org, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const allowedPages = org?.access?.pages || null;
  const allowedModules = org?.access?.modules_allowed || null;


  const roles = user?.roles || [];
  const nav = NAV.filter((n) => n.roles.some((r) => roles.includes(r)) && (n.always || !allowedPages || allowedPages.includes(n.page)));

  const prefs = user?.prefs || {};
  const isCatCollapsed = (key) => (prefs.sidebar_collapsed && key in prefs.sidebar_collapsed)
    ? prefs.sidebar_collapsed[key] : !!prefs.sidebar_default_collapsed;
  const toggleCat = async (key) => {
    const cur = { ...(prefs.sidebar_collapsed || {}) };
    cur[key] = !isCatCollapsed(key);
    try { const { data } = await api.put("/account/prefs", { sidebar_collapsed: cur }); updateUser({ ...user, prefs: data.prefs }); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    if (!org?.access) return;
    const path = location.pathname;
    const navMatch = NAV.find((n) => (n.to === "/app" ? path === "/app" : path.startsWith(n.to)));
    if (navMatch && !navMatch.always && allowedPages && !allowedPages.includes(navMatch.page)) {
      const first = NAV.find((n) => allowedPages.includes(n.page) && n.roles.some((r) => roles.includes(r)));
      navigate(first ? first.to : "/app", { replace: true });
      return;
    }
    const mMatch = path.match(/^\/app\/module\/([^/]+)/);
    if (mMatch && allowedModules && !allowedModules.includes(mMatch[1])) {
      navigate("/app", { replace: true });
    }
  }, [location.pathname, org?.access]); // eslint-disable-line

  const [counters, setCounters] = useState({});
  useEffect(() => {
    const load = () => api.get("/nav/counters").then(({ data }) => setCounters(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const compactPref = !!prefs.sidebar_compact;
  const toggleCompact = async () => {
    try { const { data } = await api.put("/account/prefs", { sidebar_compact: !compactPref }); updateUser({ ...user, prefs: data.prefs }); }
    catch { /* ignore */ }
  };
  const closeMobile = () => setMobileOpen(false);
  const openAccount = () => { navigate("/app/compte", { state: { backgroundLocation: location } }); closeMobile(); };
  const sidebarProps = {
    nav, counters, user, org, isCatCollapsed, onToggleCat: toggleCat, onToggleCompact: toggleCompact,
    onNavigate: closeMobile, onAccount: openAccount, onLogout: () => { logout(); navigate("/login"); },
  };

  return (
    <div className="dune-glow min-h-screen bg-[#09090b]">
      <TrialGate />
      <SubscriptionCelebration />
      {/* Desktop sidebar */}
      <aside data-testid="app-sidebar" className={`fixed left-0 top-0 z-30 hidden h-screen flex-col border-r border-zinc-800 bg-zinc-950 py-4 transition-[width] duration-200 md:flex ${compactPref ? "w-[76px] px-3" : "w-64 px-4"}`}>
        <Sidebar {...sidebarProps} compact={compactPref} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-72 flex-col border-r border-zinc-800 bg-zinc-950 p-4">
            <Sidebar {...sidebarProps} mobile />
          </div>
        </div>
      )}

      <div className={`relative z-10 transition-[padding] duration-200 ${compactPref ? "md:pl-[76px]" : "md:pl-64"}`}>
        <TrialBanner org={org} user={user} />
        {/* Header */}
        <header className="glass sticky top-0 z-20 flex items-center gap-3 border-b border-white/5 px-4 py-3 md:px-8">
          <button className="md:hidden text-zinc-400" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 md:hidden">
            <BrandMark />
          </div>
          <Breadcrumbs />
          <div className="ml-auto flex items-center gap-1">
            <SupportButton />
            <ThemeToggle />
            <NotificationsBell />
            <Button
              data-testid="header-new-event"
              onClick={() => navigate("/app/calendrier?new=1")}
              className="ml-2 hidden rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00] sm:inline-flex"
            >
              <Plus className="mr-1 h-4 w-4" /> Nouvel Événement
            </Button>
          </div>
        </header>

        <main className="relative min-h-[calc(100vh-61px)] px-4 py-6 pb-28 md:px-8 md:pb-10">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <div className="glass fixed bottom-4 left-4 right-4 z-40 flex items-center justify-around rounded-3xl border border-zinc-800 py-2 md:hidden">
        {nav.slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${isActive ? "text-[#FF6A00]" : "text-zinc-500"}`}>
            <n.icon className="h-5 w-5" strokeWidth={1.6} />
            {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
